import { useCallback } from "react";

import CopyFieldDropdownMenuItem from "components/CopyField/DropdownMenuItem";
import FlashContext from "components/FlashMessages/FlashContext";
import DropdownMenuEllipsis, {
  DropdownMenuEllipsisProps,
} from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import useTypedContext from "hooks/useTypedContext";
import { TrackAnalyticsEventProperties } from "shared/Analytics";
import useCanDeleteAiIntegration from "shared/AiIntegration/access/useCanDeleteAiIntegration";
import useCanToggleAiIntegration from "shared/AiIntegration/access/useCanToggleAiIntegration";
import useCanUpdateAiIntegration from "shared/AiIntegration/access/useCanUpdateAiIntegration";
import { AiIntegrationView } from "shared/AiIntegration/useSearchAiIntegrations/gql";
import useToggleAiIntegration from "shared/AiIntegration/useToggleAiIntegration";

import { showAiIntegrationDeleteConfirmation } from "../DeleteConfirmation";
import { showAiIntegrationDetailsDrawer } from "../DetailsDrawer";
import { showAiIntegrationFormDrawer } from "../FormDrawer";

type AiIntegrationsActionsDropdownProps = {
  integration: AiIntegrationView;
  trackAnalytics: (event: string, props?: TrackAnalyticsEventProperties) => void;
  hideSeeDetails?: boolean;
  onDeleteSuccess?: () => void;
  dotsSize?: DropdownMenuEllipsisProps["dotsSize"];
  refetchQueriesOnEdit: string[];
  refetchQueriesOnDelete?: string[];
};

const AiIntegrationsActionsDropdown = ({
  integration,
  trackAnalytics,
  hideSeeDetails,
  onDeleteSuccess,
  dotsSize = "small",
  refetchQueriesOnEdit,
  refetchQueriesOnDelete,
}: AiIntegrationsActionsDropdownProps) => {
  const { reportSuccess } = useTypedContext(FlashContext);
  const canUpdateAiIntegration = useCanUpdateAiIntegration();
  const canToggleAiIntegration = useCanToggleAiIntegration();
  const canDeleteAiIntegration = useCanDeleteAiIntegration();

  const [toggleAiIntegration, { loading: toggleLoading }] = useToggleAiIntegration({
    refetchQueries: refetchQueriesOnEdit,
  });

  const handleEditIntegration = useCallback(async () => {
    showAiIntegrationFormDrawer({
      integration,
      trackAnalytics,
      refetchQueriesOnEdit,
    });
  }, [integration, refetchQueriesOnEdit, trackAnalytics]);

  const handleDeleteIntegration = useCallback(() => {
    showAiIntegrationDeleteConfirmation({
      integration,
      onDeleteSuccess,
      refetchQueriesOnDelete,
    });
  }, [integration, onDeleteSuccess, refetchQueriesOnDelete]);

  const handleSeeDetails = useCallback(() => {
    showAiIntegrationDetailsDrawer({ integration, trackAnalytics });
  }, [integration, trackAnalytics]);

  const handleToggleIntegration = useCallback(() => {
    const enabled = !integration.enabled;

    toggleAiIntegration(
      {
        id: integration.id,
        enabled,
      },
      (data) => {
        if (data?.aiIntegrationToggle.id) {
          reportSuccess({
            message: `Integration successfully ${enabled ? "enabled" : "disabled"}`,
          });
          trackAnalytics("Toggle Integration", { type: "ai", enabled });
        }
      }
    );
  }, [integration, reportSuccess, toggleAiIntegration, trackAnalytics]);

  const updateAccess = canUpdateAiIntegration(integration);
  const toggleAccess = canToggleAiIntegration(integration);
  const deleteAccess = canDeleteAiIntegration(integration);

  return (
    <DropdownMenuEllipsis tooltip="Integration actions" dotsSize={dotsSize}>
      <CopyFieldDropdownMenuItem title="Copy ID" value={integration.id} />
      {!hideSeeDetails && (
        <DropdownMenuItem onAction={handleSeeDetails}>See details</DropdownMenuItem>
      )}
      <DropdownMenuItem
        onAction={handleEditIntegration}
        tooltip={updateAccess.message}
        isTooltipActive={!updateAccess.result}
        isDisabled={!updateAccess.result}
        tooltipPlacement="left"
        tooltipWidthMode="maxWidthSm"
      >
        Edit
      </DropdownMenuItem>
      <DropdownMenuItem
        onAction={handleToggleIntegration}
        loading={toggleLoading}
        tooltip={toggleAccess.message}
        isTooltipActive={!toggleAccess.result}
        isDisabled={!toggleAccess.result || toggleLoading}
        tooltipPlacement="left"
        tooltipWidthMode="maxWidthSm"
      >
        {integration.enabled ? "Disable" : "Enable"}
      </DropdownMenuItem>
      <DropdownMenuItem
        onAction={handleDeleteIntegration}
        danger
        isDisabled={!deleteAccess.result}
        tooltip={deleteAccess.message}
        isTooltipActive={!deleteAccess.result}
        tooltipPlacement="left"
        tooltipWidthMode="maxWidthSm"
      >
        Delete
      </DropdownMenuItem>
    </DropdownMenuEllipsis>
  );
};

export default AiIntegrationsActionsDropdown;
